import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { FOOTER } from '../lib/data';
import { useLanguage } from '../lib/LanguageContext';

const CONTACT_CONTENT = {
  en: {
    breadcrumbHome: 'Home',
    breadcrumbCurrent: 'Contact',
    title: 'Get in touch with us',
    hoursTitle: 'Visiting hours',
    hours: [
      { days: 'Saturday', time: '10:00 - 14:00' },
      { days: 'Sunday - Friday', time: 'By appointment' },
    ],
    enquiryTitle: 'Please send enquiry',
    enquiries: [
      'For appointments outside regular hours',
      'For exhibition visits and guided readings',
      'For sales, editions and availability',
      'For press, partnerships and curatorial requests',
    ],
    linksTitle: 'Links',
    quickLinks: [
      { label: 'Send an email', href: `mailto:${FOOTER.contact[0]}` },
      { label: 'Instagram', href: FOOTER.socials.find((s) => s.label === 'Instagram')?.href || '#' },
      { label: 'Join the mailing list', href: '#newsletter' },
      { label: 'View on Google Maps', href: '#' },
    ],
  },
  ro: {
    breadcrumbHome: 'Acasă',
    breadcrumbCurrent: 'Contact',
    title: 'Ia legătura cu noi',
    hoursTitle: 'Program de vizitare',
    hours: [
      { days: 'Sâmbătă', time: '10:00 - 14:00' },
      { days: 'Duminică - Vineri', time: 'Cu programare' },
    ],
    enquiryTitle: 'Trimite o solicitare',
    enquiries: [
      'Pentru programări în afara orelor obișnuite',
      'Pentru vizite de expoziție și tururi ghidate',
      'Pentru vânzări, ediții și disponibilitate',
      'Pentru presă, parteneriate și solicitări curatoriale',
    ],
    linksTitle: 'Linkuri',
    quickLinks: [
      { label: 'Trimite un email', href: `mailto:${FOOTER.contact[0]}` },
      { label: 'Instagram', href: FOOTER.socials.find((s) => s.label === 'Instagram')?.href || '#' },
      { label: 'Abonează-te la newsletter', href: '#newsletter' },
      { label: 'Vezi pe Google Maps', href: '#' },
    ],
  },
};

const ContactPage = () => {
  const { language } = useLanguage();
  const content = CONTACT_CONTENT[language] || CONTACT_CONTENT.en;

  return (
    <div id="top" className="bg-bone text-charcoal min-h-screen" data-testid="contact-page">
      <Header />

      <main>
        <section className="pt-36 md:pt-44 lg:pt-52 px-6 md:px-12 lg:px-16 pb-16 md:pb-24">
          <div className="grid grid-cols-12 gap-x-6 gap-y-12">
            <Reveal delay={0.08} className="col-span-12">
              <nav className="text-[11px] tracking-eyebrow uppercase text-charcoal/50 mb-6 flex items-center gap-2">
                <Link to="/" className="hover:opacity-60 transition-opacity">{content.breadcrumbHome}</Link>
                <span>/</span>
                <span className="text-charcoal/80">{content.breadcrumbCurrent}</span>
              </nav>
              <h1
                className="font-sans font-extrabold text-charcoal leading-[0.9] tracking-normal text-6xl sm:text-7xl md:text-[9vw] lg:text-[8vw]"
                data-testid="contact-page-title"
              >
                {content.title}
              </h1>
            </Reveal>
          </div>
        </section>

        <section className="px-6 md:px-12 lg:px-16 pb-20 md:pb-32">
          <div className="grid grid-cols-12 gap-x-6 gap-y-12 border-t border-charcoal pt-10 md:pt-14">
            <Reveal className="col-span-12 lg:col-span-5">
              <h2 className="font-sans font-extrabold text-3xl md:text-4xl leading-tight">
                DIMIRXSON
              </h2>
              <address className="mt-8 not-italic text-xl md:text-2xl leading-snug font-light text-charcoal/85">
                {FOOTER.address.map((line) => (
                  <span key={line} className="block">{line}</span>
                ))}
              </address>
              <div className="mt-8 space-y-2 text-base md:text-lg font-light text-charcoal/80">
                {FOOTER.contact.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </div>
            </Reveal>

            <div className="col-span-12 lg:col-span-6 lg:col-start-7">
              <Reveal>
                <ContactBlock title={content.hoursTitle}>
                  <div className="space-y-5">
                    {content.hours.map((item) => (
                      <div key={item.days} className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 sm:col-span-5 text-[11px] tracking-eyebrow uppercase text-charcoal/50">
                          {item.days}
                        </div>
                        <div className="col-span-12 sm:col-span-7 text-2xl md:text-3xl font-light uppercase leading-none">
                          {item.time}
                        </div>
                      </div>
                    ))}
                  </div>
                </ContactBlock>
              </Reveal>

              <Reveal delay={0.1}>
                <ContactBlock title={content.enquiryTitle}>
                  <ul className="space-y-4">
                    {content.enquiries.map((item) => (
                      <li key={item} className="flex gap-3 text-base md:text-lg font-light text-charcoal/80">
                        <span aria-hidden="true">→</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </ContactBlock>
              </Reveal>

              <Reveal delay={0.18}>
                <ContactBlock title={content.linksTitle}>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                    {content.quickLinks.map((link) => (
                      <li key={link.label}>
                        <a
                          href={link.href}
                          className="editorial-link"
                          target={link.href.startsWith('http') ? '_blank' : undefined}
                          rel={link.href.startsWith('http') ? 'noreferrer' : undefined}
                        >
                          <span>{link.label}</span>
                          <span className="arrow">→</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </ContactBlock>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="px-6 md:px-12 lg:px-16 pb-24 md:pb-36">
          <Reveal>
            <div className="relative w-full aspect-[16/9] md:aspect-[16/6] overflow-hidden bg-beige">
              <img
                src="/images/image5.png"
                alt="DIMIRXSON gallery interior"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </Reveal>
        </section>
      </main>

      <Footer />
    </div>
  );
};

const ContactBlock = ({ title, children }) => (
  <section className="border-t border-charcoal py-8 md:py-10">
    <h2 className="text-[11px] tracking-eyebrow uppercase text-charcoal/55 mb-7">
      {title}
    </h2>
    {children}
  </section>
);

export default ContactPage;
